'use client';

import { Bell } from '@phosphor-icons/react/ssr';
import {
  type ComponentProps,
  type ElementType,
  type ReactNode,
  useId,
  useState,
} from 'react';

import { tv } from '../../lib/tv';
import { cn } from '../../lib/utils';
import {
  Popover,
  PopoverContent as PopoverContentPrimitive,
  PopoverTrigger,
} from '../ui/popover';
import { Link } from './link';
import { popoverStyles } from './overlay-styles';
import { Tab, TabList, TabPanel, Tabs } from './tabs';
import { TopBarNotifications } from './top-bar';

export type NotificationTone = 'success' | 'info' | 'warning' | 'danger';

/**
 * Figma Notification Item (100:12428): 400 wide, 12 · 16 padding, gap 12,
 * top-aligned. Person: a 32px Avatar; System: a 32px status/<tone>/bg circle
 * with a 16px status/<tone>/fg glyph. Content (gap 6): Message Body/Small
 * with names and objects in SemiBold; an optional Quote (bg/subtle, 2px
 * border/strong left stroke in the layout, radius/sm, 6 · 10, Body/Small
 * text/secondary); Meta (gap 8): Caption text/tertiary time, optional Small
 * Brand Link action. Unread: text/primary and an 8px brand/strong dot
 * (in an 8 × 20 column); Read: text/secondary, the dot hidden but its space
 * kept. Hover: bg/subtle.
 */
export const notificationItemStyles = tv({
  slots: {
    root: 'relative flex w-full items-start gap-3 px-4 py-3 transition-colors duration-[120ms] ease-out',
    leading: 'flex shrink-0',
    tile: 'flex size-8 shrink-0 items-center justify-center rounded-full',
    tileIcon: 'size-4',
    content: 'flex min-w-0 flex-1 flex-col gap-1.5',
    message: 'text-body-sm [&_strong]:font-semibold',
    // The whole row is the target: the link's ::after covers it (and draws the focus ring).
    target: [
      'outline-none after:absolute after:inset-0 after:content-[""]',
      'focus-visible:after:inset-ring-2 focus-visible:after:inset-ring-border-focus',
      'cursor-pointer text-left',
    ],
    quote:
      'bg-bg-subtle text-body-sm text-text-secondary rounded-sm border-l-2 border-border-strong px-2.5 py-1.5',
    meta: 'flex items-center gap-2',
    time: 'text-caption text-text-tertiary',
    // Above the row target so it stays clickable.
    action: 'relative z-10 h-5',
    dotColumn: 'flex h-5 w-2 shrink-0 items-center',
    dot: 'bg-brand-strong size-2 rounded-full',
  },
  variants: {
    unread: {
      true: { message: 'text-text-primary' },
      false: { message: 'text-text-secondary', dot: 'opacity-0' },
    },
    interactive: { true: { root: 'hover:bg-bg-subtle' } },
    tone: {
      success: { tile: 'bg-status-success-bg text-status-success-fg' },
      info: { tile: 'bg-status-info-bg text-status-info-fg' },
      warning: { tile: 'bg-status-warning-bg text-status-warning-fg' },
      danger: { tile: 'bg-status-danger-bg text-status-danger-fg' },
    },
  },
  defaultVariants: { unread: false, interactive: false, tone: 'success' },
});

export type NotificationItemProps = Omit<
  ComponentProps<'div'>,
  'children' | 'onSelect'
> & {
  /** Figma State: Unread (primary text + dot) or Read. Announced as "Unread". */
  unread?: boolean;
  /** Figma Kind=Person: a 32px Avatar, e.g. `<Avatar size={32} name="Maya Kowalski" alt="" />`. */
  avatar?: ReactNode;
  /** Figma Kind=System: the status glyph (default CheckCircle in the story) in a tone circle. */
  icon?: ElementType<{ className?: string }>;
  /** System tile tone. Default success. */
  tone?: NotificationTone;
  /** Figma "Message": who did what - wrap names and objects in `<strong>`. */
  message: ReactNode;
  /** Figma "Quote": the quoted snippet (a comment). */
  quote?: ReactNode;
  /** Relative time, e.g. "2m ago". */
  time: ReactNode;
  /** Machine-readable time for the `<time>` element. */
  dateTime?: string;
  /** What kind of event, after the time: "2m ago · Comment". */
  category?: ReactNode;
  /** Figma System "Action": a Small Brand Link, e.g. `<Link size="sm" href="…">Review</Link>`. */
  action?: ReactNode;
  /** Makes the row a link to the object (marks it read in your handler). */
  href?: string;
  /** Makes the row a button. Called on click / Enter - mark the item read here. */
  onSelect?: () => void;
};

/**
 * One notification (Figma: Components ↳ Notifications ↳ Notification Item).
 * Person (avatar + who did what + quoted snippet) or System (status icon +
 * outcome + action). With `href` / `onSelect` the whole row is the target
 * (the message is the link or button, stretched over the row); the action
 * stays a separate tab stop. Render it in a list item (NotificationCenter
 * does).
 */
export function NotificationItem({
  unread = false,
  avatar,
  icon: Icon,
  tone,
  message,
  quote,
  time,
  dateTime,
  category,
  action,
  href,
  onSelect,
  className,
  ...props
}: NotificationItemProps) {
  const interactive = Boolean(href || onSelect);
  const styles = notificationItemStyles({ unread, interactive, tone });
  const text = (
    <>
      {unread && <span className="sr-only">Unread: </span>}
      {message}
    </>
  );
  return (
    <div
      data-slot="notification-item"
      data-unread={unread || undefined}
      className={styles.root({ className })}
      {...props}
    >
      {avatar ? (
        <span data-slot="notification-item-avatar" className={styles.leading()}>
          {avatar}
        </span>
      ) : (
        Icon && (
          <span aria-hidden data-slot="notification-item-tile" className={styles.tile()}>
            <Icon className={styles.tileIcon()} />
          </span>
        )
      )}
      <div data-slot="notification-item-content" className={styles.content()}>
        <p data-slot="notification-item-message" className={styles.message()}>
          {href ? (
            <a
              href={href}
              data-slot="notification-item-target"
              className={styles.target()}
              onClick={onSelect}
            >
              {text}
            </a>
          ) : onSelect ? (
            <button
              type="button"
              data-slot="notification-item-target"
              className={styles.target()}
              onClick={onSelect}
            >
              {text}
            </button>
          ) : (
            text
          )}
        </p>
        {quote && (
          <blockquote data-slot="notification-item-quote" className={styles.quote()}>
            {quote}
          </blockquote>
        )}
        <div data-slot="notification-item-meta" className={styles.meta()}>
          <span data-slot="notification-item-time" className={styles.time()}>
            <time dateTime={dateTime}>{time}</time>
            {category && <> · {category}</>}
          </span>
          {action && (
            <span data-slot="notification-item-action" className={styles.action()}>
              {action}
            </span>
          )}
        </div>
      </div>
      <span
        aria-hidden
        data-slot="notification-item-dot-column"
        className={styles.dotColumn()}
      >
        <span data-slot="notification-item-dot" className={styles.dot()} />
      </span>
    </div>
  );
}

/** One notification in the center: an item plus where it is listed. */
export type NotificationEntry = Omit<NotificationItemProps, 'onSelect'> & {
  id: string;
  /** Figma group label ("Today", "Earlier"). Groups show in first-seen order. */
  group?: string;
  /** Listed under the Mentions tab too. */
  mention?: boolean;
};

export type NotificationTab = 'all' | 'mentions';

/**
 * Figma Notification Center (100:12578): the Popover surface (bg/surface,
 * 1px border/subtle inside, radius/lg, Shadow/MD, clipped), 400 wide, max 560
 * then the list scrolls. Header 44 (14 · 16 · 10 · 16: Label/Large title,
 * Small Brand "Mark all as read" - Disabled when nothing is unread) ·
 * Underline Tab List (12 left; All with the unread count, Mentions) ·
 * Groups (32: Overline text/tertiary, 12 · 0 · 4 · 16) of Notification
 * Items · Footer 43 (1px border/subtle top, 10 · 12, a centred Small Brand
 * "View all notifications"). Empty: 40 · 48, gap 6: a 48px brand/subtle
 * circle with a 22px icon/brand bell, Body/Medium Strong title, Body/Small
 * text/secondary body.
 */
export const notificationCenterStyles = tv({
  slots: {
    root: 'w-100 max-h-[min(560px,var(--radix-popover-content-available-height,560px))]',
    header: 'flex h-11 shrink-0 items-center pt-3.5 pr-4 pb-2.5 pl-4',
    title: 'text-label-lg text-text-primary min-w-0 flex-1',
    link: 'h-5',
    tabs: 'flex min-h-0 flex-1 flex-col',
    tabList: 'shrink-0 pl-3',
    panel:
      'min-h-0 flex-1 overflow-y-auto outline-none focus-visible:inset-ring-2 focus-visible:inset-ring-border-focus',
    group:
      'text-overline text-text-tertiary flex h-8 items-start pt-3 pb-1 pl-4 uppercase',
    footer: 'flex shrink-0 justify-center border-t border-border-subtle pt-2.5 pb-3',
    empty: 'flex flex-col items-center gap-1.5 pt-10 pb-12 text-center',
    emptyIcon:
      'bg-brand-subtle text-icon-brand mb-0 flex size-12 items-center justify-center rounded-full',
    emptyGlyph: 'size-[22px]',
    emptyTitle: 'text-body-md-strong text-text-primary',
    emptyBody: 'text-body-sm text-text-secondary',
  },
});

type SectionProps = {
  notifications: NotificationEntry[];
  /** Selected tab (controlled). */
  tab?: NotificationTab;
  defaultTab?: NotificationTab;
  onTabChange?: (tab: NotificationTab) => void;
  /** "Mark all as read". Disabled when nothing is unread. */
  onMarkAllRead?: () => void;
  /** Called when a notification is opened - mark it read here (opening the center doesn’t). */
  onNotificationSelect?: (notification: NotificationEntry) => void;
  /** Footer "View all notifications" link. */
  viewAllHref?: string;
  /** Takes over the footer link (e.g. client-side navigation that also closes the popover). */
  onViewAll?: () => void;
  /** Unread total from the server, when there are more than the entries shown. */
  unreadCount?: number;
  title?: string;
  emptyTitle?: ReactNode;
  emptyBody?: ReactNode;
};

function NotificationCenterSections({
  titleId,
  notifications,
  tab: tabProp,
  defaultTab = 'all',
  onTabChange,
  onMarkAllRead,
  onNotificationSelect,
  viewAllHref,
  onViewAll,
  unreadCount,
  title = 'Notifications',
  emptyTitle = 'You’re all caught up',
  emptyBody = 'Mentions, renders and invites will show up here.',
}: SectionProps & { titleId: string }) {
  const styles = notificationCenterStyles();
  const [tabState, setTabState] = useState(defaultTab);
  const tab = tabProp ?? tabState;
  const unread = unreadCount ?? notifications.filter((n) => n.unread).length;

  const panel = (value: NotificationTab) => {
    const shown =
      value === 'mentions' ? notifications.filter((n) => n.mention) : notifications;
    const groups: { label: string; items: NotificationEntry[] }[] = [];
    for (const n of shown) {
      const label = n.group ?? '';
      const found = groups.find((g) => g.label === label);
      if (found) found.items.push(n);
      else groups.push({ label, items: [n] });
    }
    return (
      <TabPanel
        value={value}
        data-slot="notification-center-panel"
        className={styles.panel()}
      >
        {shown.length === 0 ? (
          <div data-slot="notification-center-empty" className={styles.empty()}>
            <span aria-hidden className={styles.emptyIcon()}>
              <Bell className={styles.emptyGlyph()} />
            </span>
            <p className={styles.emptyTitle()}>{emptyTitle}</p>
            <p className={styles.emptyBody()}>{emptyBody}</p>
          </div>
        ) : (
          groups.map((group, gi) => (
            <NotificationGroup key={group.label || gi} label={group.label}>
              {group.items.map(({ id, group: _group, mention: _mention, ...item }) => (
                <li key={id}>
                  <NotificationItem
                    {...item}
                    onSelect={
                      onNotificationSelect
                        ? () =>
                            onNotificationSelect(notifications.find((n) => n.id === id)!)
                        : undefined
                    }
                  />
                </li>
              ))}
            </NotificationGroup>
          ))
        )}
      </TabPanel>
    );
  };

  const tabHasItems =
    tab === 'mentions' ? notifications.some((n) => n.mention) : notifications.length > 0;

  return (
    <>
      <div data-slot="notification-center-header" className={styles.header()}>
        <h2 id={titleId} data-slot="notification-center-title" className={styles.title()}>
          {title}
        </h2>
        <Link
          asChild
          size="sm"
          disabled={!unread}
          data-slot="notification-center-mark-all"
          className={styles.link()}
        >
          <button type="button" disabled={!unread} onClick={() => onMarkAllRead?.()}>
            <span data-disabled={!unread ? '' : undefined}>Mark all as read</span>
          </button>
        </Link>
      </div>
      <Tabs
        value={tab}
        onValueChange={(next) => {
          setTabState(next as NotificationTab);
          onTabChange?.(next as NotificationTab);
        }}
        className={styles.tabs()}
      >
        <TabList aria-label={title} className={styles.tabList()}>
          <Tab
            value="all"
            count={
              unread ? (
                <>
                  {unread}
                  <span className="sr-only"> unread</span>
                </>
              ) : undefined
            }
          >
            All
          </Tab>
          <Tab value="mentions">Mentions</Tab>
        </TabList>
        {panel('all')}
        {panel('mentions')}
      </Tabs>
      {viewAllHref && tabHasItems && (
        <div data-slot="notification-center-footer" className={styles.footer()}>
          <Link
            size="sm"
            href={viewAllHref}
            className={styles.link()}
            onClick={(event) => {
              if (!onViewAll) return;
              event.preventDefault();
              onViewAll();
            }}
          >
            View all notifications
          </Link>
        </div>
      )}
    </>
  );
}

/** A labelled group of notifications ("Today"): an Overline heading over a list. */
export function NotificationGroup({
  label,
  children,
}: {
  label?: string;
  children: ReactNode;
}) {
  const id = useId();
  const styles = notificationCenterStyles();
  return (
    <section aria-labelledby={label ? id : undefined} data-slot="notification-group">
      {label && (
        <h3 id={id} data-slot="notification-group-label" className={styles.group()}>
          {label}
        </h3>
      )}
      <ul data-slot="notification-list">{children}</ul>
    </section>
  );
}

export type NotificationCenterProps = SectionProps & {
  /** Open state (uncontrolled by default). */
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** The bell. Default: `TopBarNotifications` with the unread dot when anything is unread. */
  trigger?: ReactNode;
  /** Which side of the trigger the panel opens on. Default bottom (under the Top Bar bell). */
  side?: 'top' | 'right' | 'bottom' | 'left';
  /** Alignment against the trigger. Default end; use start beside a sidebar item. */
  align?: 'start' | 'center' | 'end';
  className?: string;
};

/**
 * The inbox behind the Top Bar bell (Figma: Components ↳ Notifications ↳
 * Notification Center): a Radix Popover 8px below the bell, end-aligned
 * (or beside a sidebar item with `side="right" align="start"`), named by its
 * "Notifications" title. Tabs All / Mentions, groups (Today /
 * Earlier) as headed lists, Mark all as read, View all. Opening doesn’t mark
 * anything read - `onNotificationSelect` and `onMarkAllRead` tell you when
 * to; the app owns each entry's `unread`.
 */
export function NotificationCenter({
  open,
  defaultOpen,
  onOpenChange,
  trigger,
  side = 'bottom',
  align = 'end',
  className,
  ...sections
}: NotificationCenterProps) {
  const titleId = useId();
  const hasUnread =
    (sections.unreadCount ?? 0) > 0 || sections.notifications.some((n) => n.unread);
  return (
    <Popover open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        {trigger ?? <TopBarNotifications unread={hasUnread} />}
      </PopoverTrigger>
      <PopoverContentPrimitive
        surface="panel"
        side={side}
        align={align}
        sideOffset={8}
        collisionPadding={8}
        aria-labelledby={titleId}
        data-slot="notification-center"
        className={popoverStyles({ size: 'lg' }).root({
          className: cn(notificationCenterStyles().root(), className),
        })}
      >
        <NotificationCenterSections titleId={titleId} {...sections} />
      </PopoverContentPrimitive>
    </Popover>
  );
}

export type NotificationCenterPanelProps = SectionProps & { className?: string };

/** The center drawn in place (docs, the Figma frame) - same classes, no popover. */
export function NotificationCenterPanel({
  className,
  ...sections
}: NotificationCenterPanelProps) {
  const titleId = useId();
  return (
    <div
      role="group"
      aria-labelledby={titleId}
      data-slot="notification-center"
      className={popoverStyles({ size: 'lg' }).root({
        className: cn(notificationCenterStyles().root(), className),
      })}
    >
      <NotificationCenterSections titleId={titleId} {...sections} />
    </div>
  );
}
